<script setup lang="ts">
import {
  computed,
  inject,
  onMounted,
  toRefs,
} from 'vue';

import { PROJECT_ICONS_KEY } from '@/core/plugins/icons.ts';
import { SvgIcon } from './types.ts';
// import iconsJson from '@/assets/icons.json';

const props = withDefaults(defineProps<{
  name?: string;
  fallbackName?: string;
  stroked?: boolean;
  clickable?: boolean;
  class?: string | (string | undefined)[] | Record<string, unknown>;
  icon?: SvgIcon | null;
}>(), {
  name: '',
  icon: null,
  class: '',
  fallbackName: 'not-found',
});

const { name, fallbackName } = toRefs(props);

const icons = inject<Record<string, SvgIcon>>(PROJECT_ICONS_KEY);

const computedName = computed(() => icons?.[name.value] ? name.value : fallbackName.value);
const _icon = computed<SvgIcon | null>(() => props.icon ?? icons?.[computedName.value] ?? null);

const rootClass = computed(() => {
  let classArray: string[] = [];
  if (Array.isArray(props.class)) {
    classArray = props.class.filter(Boolean) as string[];
  } else if (typeof props.class === 'object') {
    classArray = Object.entries(props.class)
      .filter(([, value]) => Boolean(value))
      .map(([key]) => key);
  } else {
    classArray = [props.class];
  }

  return classArray.concat('icon');
});

onMounted(() => {
  if (!_icon.value) {
    console.error(`SvgIcon not set with 'icon' prop, or not found in the icons.json list: ${name.value}`);
  }
});

</script>

<template>
  <svg
    v-if="computedName && _icon && _icon.viewBox && _icon.name"
    :viewBox="_icon.viewBox"
    :class="[rootClass, {
      'stroked': stroked || _icon.stroked,
      'hover:cursor-pointer': clickable,
      [`icon--${_icon.name}`]: _icon.name,
    }]"
    v-html="_icon.contents"
  />
</template>

<style lang="postcss" scoped>
.icon {
  fill: none;
  vertical-align: middle;
  width: 1em;
  height: 1em;
}
</style>

